import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { saveMessage } from '../utils/storage';
import { filterProfanity, hasProfanity } from '../utils/profanityFilter';

const { FiSend, FiHeart, FiEye, FiEyeOff } = FiIcons;

const KindnessForm = ({ kindnessId }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [showSender, setShowSender] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Please write a kind message');
      return;
    }

    if (message.length > 1000) {
      setError('Message must be 1000 characters or less');
      return;
    }

    if (hasProfanity(message) || (title && hasProfanity(title))) {
      setError('Please keep your message positive and kind');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const cleanMessage = filterProfanity(message);
      const cleanTitle = title ? filterProfanity(title) : '';
      const messageData = {
        title: cleanTitle,
        content: cleanMessage,
        sender: showSender ? kindnessId : 'Anonymous',
        isAnonymous: !showSender
      };

      // Using await here to ensure we handle any errors properly
      const messageId = await saveMessage(messageData);
      navigate(`/message/${messageId}`);
    } catch (err) {
      console.error('Error saving message:', err);
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const usedChars = message.length;
  const maxChars = 1000;
  const remainingChars = maxChars - usedChars;

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.5 }}
      className="clay-card mobile-spacing"
    >
      <div className="flex items-center space-x-4 mb-8">
        <div className="clay-button-pink p-4">
          <SafeIcon icon={FiHeart} className="text-2xl heart-logo" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-heading">Send Anonymous Kindness</h2>
          <p className="text-text font-medium">Share a compliment, encouragement, or positive message</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Optional Title Field */}
        <div>
          <label htmlFor="message-title" className="block text-lg font-bold text-heading mb-3">
            Optional Title
          </label>
          <input
            id="message-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Give your message a title (optional)"
            className="clay-input w-full form-title-input text-text placeholder-gray-500 font-medium focus-visible"
            maxLength={100}
          />
        </div>

        {/* Message Content Field */}
        <div>
          <label htmlFor="message-content" className="block text-lg font-bold text-heading mb-3">
            Your Message
          </label>
          <textarea
            id="message-content"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write something kind and uplifting... ✨"
            className="clay-input w-full form-message-input text-text placeholder-gray-500 resize-none focus-visible font-medium"
            maxLength={maxChars}
            required
          />
          <div className="flex justify-between items-center mt-3">
            <span className={`text-sm font-bold ${remainingChars < 50 ? 'text-red-600' : 'text-text'}`}>
              {usedChars} / {maxChars} characters
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => setShowSender(!showSender)}
            className={`clay-button-yellow px-6 py-4 flex items-center justify-center space-x-3 transition-all duration-300 focus-visible ${
              showSender ? 'transform scale-105' : ''
            }`}
          >
            <SafeIcon icon={showSender ? FiEye : FiEyeOff} className="text-xl text-text" />
            <span className="font-bold text-text">
              {showSender ? `Show as ${kindnessId?.split('-').slice(0, 2).join('-')}...` : 'Send anonymously'}
            </span>
          </button>

          <button
            type="submit"
            disabled={isSubmitting || !message.trim()}
            className={`clay-button-green px-8 py-4 flex items-center justify-center space-x-3 font-bold transition-all duration-300 focus-visible ${
              isSubmitting || !message.trim() 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:transform hover:scale-105'
            }`}
          >
            <SafeIcon icon={FiSend} className="text-xl text-text" />
            <span className="text-text text-lg">{isSubmitting ? 'Sending...' : 'Send Kindness'}</span>
          </button>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="clay-card bg-pink p-4 border-l-4 border-red-400"
          >
            <p className="text-red-700 font-bold">{error}</p>
          </motion.div>
        )}
      </form>

      <div className="mt-8 clay-card bg-green p-4">
        <p className="text-text text-center font-medium">
          💝 Your message will create a unique shareable link that you can send to brighten someone's day!
        </p>
      </div>
    </motion.div>
  );
};

export default KindnessForm;